import React, { useLayoutEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useDesign } from '@/contexts/DesignContext';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar as CalendarIcon, RotateCcw, Repeat, Users, X, Clock, Flag } from 'lucide-react';
import { format } from 'date-fns';
import { Calendar } from '@/components/ui/calendar';
import { generateTimeOptions, formatTime12h } from '../../../utils/timeFormat';
import MentionSuggestions from '@/components/shared/MentionSuggestions';
import { SubtaskEditor } from '@/components/shared/Subtasks';
import { recurrencePayloadFields, recurrenceStateFromRow, describeRecurrence } from '@/lib/recurrenceState';

const TIME_OPTIONS = generateTimeOptions();

// ── Shared Task State & Logic ──

export const INITIAL_TASK_STATE = {
  title: '', description: '', time: '', duration_minutes: null, priority: 'none',
  date: null, // 'yyyy-MM-dd' picked in the Add dialog; null = the day the dialog was opened from
  assigned_to: null, assigned_to_name: '',
  subtasks: [],
  isRecurring: false, recurrenceType: 'weekly', recurrenceInterval: 1,
  recurrenceDaysOfWeek: [], recurrenceDayOfMonth: 1,
  recurrenceMonthlyMode: 'day', recurrenceWeekday: 'friday', recurrenceNth: 1,
  recurrenceEndType: 'never', recurrenceEndDate: '', recurrenceCount: 10
};

// The recurrence state ↔ payload mappers now live in lib/recurrenceState.js so
// non-dialog code (shared tasks, hooks, tests) can use them without pulling in
// React. Re-exported here because every existing importer reads them from this
// module.
export { recurrencePayloadFields, recurrenceStateFromRow, describeRecurrence };

// Duration presets shared by Add / Edit dialogs (minutes).
// Visible only when the task has a Time selected.
export const DURATION_OPTIONS = [
  { value: 15, label: '15 min' },
  { value: 30, label: '30 min' },
  { value: 45, label: '45 min' },
  { value: 60, label: '1 hour' },
  { value: 90, label: '1.5 hours' },
  { value: 120, label: '2 hours' },
  { value: 180, label: '3 hours' },
  { value: 240, label: '4 hours' },
];

/**
 * Create a task (recurring or non-recurring). Returns array of created tasks.
 * Caller handles state updates (setTasks, setShowAddTask, etc.)
 */
export async function createPlannerTask({ userId, newTask, date, supabase }) {
  if (!newTask.title.trim()) throw new Error('Title is required');
  // A date picked in the Add dialog wins over the caller's default day, so no
  // consumer can show one date and write another.
  date = newTask.date || date;

  if (newTask.isRecurring) {
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/planner/tasks/create-recurring`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        user_id: userId,
        title: newTask.title,
        description: newTask.description || null,
        date,
        time: newTask.time || null,
        duration_minutes: newTask.time ? (newTask.duration_minutes || null) : null,
        priority: newTask.priority,
        ...(newTask.subtasks?.length ? { subtasks: newTask.subtasks.map(s => ({ id: s.id, title: s.title, completed: false })) } : {}),
        ...recurrencePayloadFields(newTask),
      })
    });
    const result = await response.json();
    if (!response.ok || !result.success) throw new Error(result.detail || 'Failed to create recurring task');
    return [result.parent, ...(result.instances || [])];
  } else {
    const payload = {
      user_id: userId,
      title: newTask.title,
      description: newTask.description || '',
      date,
      time: newTask.time || null,
      priority: newTask.priority,
      completed: false,
      is_recurring: false,
    };
    // Only include duration_minutes when we actually have one — keeps payload
    // safe on Supabase schemas that haven't yet added the column.
    if (newTask.time && newTask.duration_minutes) {
      payload.duration_minutes = newTask.duration_minutes;
    }
    const { data, error } = await supabase.from('tasks').insert([payload]).select().single();
    if (error) throw error;
    return [data];
  }
}

// ── Compact UI helper: pill-styled select triggers ──
const PILL_CLS = "studio-pill h-8 w-auto gap-1.5 rounded-full border-indigo-200 dark:border-indigo-500/40 text-indigo-600 dark:text-indigo-300 text-xs font-medium px-3 bg-indigo-50/50 dark:bg-indigo-500/10 hover:bg-indigo-100/60 dark:hover:bg-indigo-500/20 transition-colors [&>svg:last-child]:hidden";

// ── Assignee pill + chip (shared by the Add and Edit dialogs; R34) ──
// `members` empty → renders nothing (non-admins never see it).
function AssigneeControls({ value, valueName, onChange, members = [], currentUserId, testIdPrefix }) {
  if (!members.length) return null;
  if (!value) {
    return (
      <Select value="" onValueChange={(uid) => {
        const m = members.find(x => x.user_id === uid);
        if (m) onChange(m);
      }}>
        <SelectTrigger className={PILL_CLS} data-testid={`${testIdPrefix}-assignee-trigger`}>
          <Users className="w-3 h-3" />
          <SelectValue placeholder="Assign" />
        </SelectTrigger>
        <SelectContent className="max-h-60">
          {members.map(m => (
            <SelectItem key={m.user_id} value={m.user_id} data-testid={`assignee-option-${m.user_id}`}>
              {m.user_name}{m.user_id === currentUserId ? ' (me)' : ''}{['owner', 'admin'].includes(m.role) ? ' · admin' : ''}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }
  return (
    <div className="flex items-center gap-2 h-8 px-3 rounded-full border border-violet-300 dark:border-violet-500/40 bg-violet-50 dark:bg-violet-500/10" data-testid={`${testIdPrefix}-assignee-chip`}>
      <Users className="w-3 h-3 text-violet-500" />
      <span className="text-xs font-medium text-violet-700 dark:text-violet-300 truncate max-w-[140px]">{valueName}</span>
      <button type="button" onClick={() => onChange(null)} className="text-violet-400 hover:text-violet-600" aria-label="Remove assignee" data-testid={`${testIdPrefix}-clear-assignee`}>
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}

// ── Scheduled date (shared by the Add and Edit dialogs, 2026-09-09) ──
// Inline calendar rather than a popover: Safari drops nested portals inside a
// Dialog. `value` is 'yyyy-MM-dd' or null; `fallbackDate` is shown while
// nothing has been picked (Add: the day the dialog was opened from).
function ScheduledDateField({ value, fallbackDate = null, onChange, disablePast = false, size = 'md', testIdPrefix = 'task' }) {
  const [open, setOpen] = useState(false);
  const { isStudio } = useDesign();
  const shown = value ? new Date(value + 'T00:00:00') : fallbackDate;
  const compact = size === 'sm';
  return (
    <div className={compact ? '' : 'space-y-2'}>
      <Label className={isStudio ? 'text-[13px] font-medium text-foreground mb-1.5 block' : (compact ? 'text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5 block' : 'text-sm font-medium')}>{isStudio ? 'Scheduled date' : 'Scheduled Date'}</Label>
      <Button
        variant="outline"
        className={`w-full justify-start text-left font-normal ${compact ? 'h-9' : 'h-11'}`}
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        data-testid={`${testIdPrefix}-date-btn`}
      >
        <CalendarIcon className="mr-2 h-4 w-4 text-slate-400 dark:text-slate-500" />
        {shown ? format(shown, 'EEEE, MMMM d, yyyy') : 'Pick a date'}
      </Button>
      {open && (
        <div className={`border rounded-xl p-2 bg-card shadow-sm ${compact ? 'mt-2' : ''}`}>
          <Calendar
            mode="single"
            required
            selected={shown || undefined}
            onSelect={(date) => {
              // `required`: tapping the already-highlighted day re-emits it
              // instead of clearing, so "yes, this day" always closes the picker.
              if (date) onChange(format(date, 'yyyy-MM-dd'));
              setOpen(false);
            }}
            disabled={disablePast ? { before: new Date() } : undefined}
            initialFocus
          />
        </div>
      )}
    </div>
  );
}

// ── Add New Task Dialog ──

export function AddTaskDialog({ open, onOpenChange, newTask, setNewTask, onSubmit, selectedDate, members = [], currentUserId }) {
  const { isStudio } = useDesign();
  // @name quick-assign in the title input (same popup as comment mentions)
  const [mentionQuery, setMentionQuery] = useState(null);
  const mentionSuggestionsRef = useRef(null);
  const titleInputRef = useRef(null);
  const canAssign = members.length > 0;

  // The task lands on the day the dialog was opened from unless a date is
  // picked. Clear a leftover pick each time the dialog opens so a cancelled
  // draft cannot silently carry yesterday's choice onto another day. Layout
  // effect: it must run before the first paint of the reopened dialog, or the
  // old date flashes in the header during the open animation. Any future
  // caller that pre-fills `date` before opening must set it after the open.
  useLayoutEffect(() => {
    if (open) setNewTask((prev) => (prev.date ? { ...prev, date: null } : prev));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const effectiveDate = newTask.date ? new Date(newTask.date + 'T00:00:00') : selectedDate;

  const detectMention = (value, caret) => {
    const upto = value.slice(0, caret ?? value.length);
    const m = upto.match(/(?:^|\s)@(\w*)$/);
    setMentionQuery(m ? m[1] : null);
  };

  const handleTitleChange = (e) => {
    setNewTask({ ...newTask, title: e.target.value });
    if (canAssign) detectMention(e.target.value, e.target.selectionStart);
  };

  const handleMentionSelect = (item) => {
    if (item.kind !== 'user') return;
    const el = titleInputRef.current;
    const caret = el?.selectionStart ?? newTask.title.length;
    const upto = newTask.title.slice(0, caret);
    const stripped = upto.replace(/(^|\s)@\w*$/, '$1');
    const cleanTitle = (stripped + newTask.title.slice(caret)).replace(/\s+$/, ' ');
    setNewTask({
      ...newTask,
      title: cleanTitle,
      assigned_to: item.user_id,
      assigned_to_name: item.user_name,
    });
    setMentionQuery(null);
    setTimeout(() => el?.focus(), 0);
  };

  const clearAssignee = () => setNewTask({ ...newTask, assigned_to: null, assigned_to_name: '' });

  const handleKeyDown = (e) => {
    if (mentionQuery !== null && mentionSuggestionsRef.current?.handleKeyDown(e)) return;
    if (e.key === 'Escape' && mentionQuery !== null) { setMentionQuery(null); e.stopPropagation(); return; }
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      onSubmit();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`${isStudio ? 'sm:max-w-[440px]' : 'sm:max-w-[425px]'} p-0 gap-0 overflow-hidden max-h-[85vh] flex flex-col`} data-testid="add-task-dialog">
        <DialogHeader className={isStudio ? 'px-5 pt-4 pb-3 flex-shrink-0' : 'px-5 pt-5 pb-3 flex-shrink-0'}>
          <DialogTitle className="text-lg">{isStudio ? 'Add task' : 'Add New Task'}</DialogTitle>
          <DialogDescription className="text-sm">
            {format(effectiveDate, 'EEEE, MMMM d')}
          </DialogDescription>
        </DialogHeader>
        <div className="px-5 pb-4 space-y-3 overflow-y-auto flex-1">
          <div>
            <Label className={isStudio ? 'text-[13px] font-medium text-foreground mb-1.5 block' : 'text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5 block'}>{isStudio ? 'Task title' : 'Task Title'}</Label>
            <div className="relative">
              {mentionQuery !== null && canAssign && (
                <MentionSuggestions
                  ref={mentionSuggestionsRef}
                  query={mentionQuery}
                  members={members}
                  groups={[]}
                  onSelect={handleMentionSelect}
                  currentUserId={currentUserId}
                />
              )}
              <Input
                ref={titleInputRef}
                placeholder={canAssign ? 'What needs to be done? (@ to assign)' : 'What needs to be done?'}
                value={newTask.title}
                onChange={handleTitleChange}
                onKeyDown={handleKeyDown}
                className="h-9"
                data-testid="add-task-title-input"
              />
            </div>
          </div>
          <div>
            <Label className={isStudio ? 'text-[13px] font-medium text-foreground mb-1.5 block' : 'text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5 block'}>Description</Label>
            <Textarea
              placeholder="Add details (optional)"
              value={newTask.description}
              onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
              onKeyDown={handleKeyDown}
              className={isStudio ? 'min-h-[88px] resize-none' : 'min-h-[60px] resize-none'}
            />
          </div>
          <ScheduledDateField
            value={newTask.date}
            fallbackDate={selectedDate}
            onChange={(date) => setNewTask({ ...newTask, date })}
            size="sm"
            testIdPrefix="add-task"
          />
          {/* Compact pill row: time / duration / priority / assign */}
          <div className="flex flex-wrap items-center gap-2">
            {!newTask.assigned_to && (
              <Select value={newTask.time || 'none'} onValueChange={(v) => setNewTask({ ...newTask, time: v === 'none' ? '' : v, duration_minutes: v === 'none' ? null : (newTask.duration_minutes || 60) })}>
                <SelectTrigger className={PILL_CLS} data-testid="add-task-time-pill">
                  <Clock className="w-3 h-3" />
                  <SelectValue>{newTask.time ? formatTime12h(newTask.time) : 'No time'}</SelectValue>
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  <SelectItem value="none">No time</SelectItem>
                  {TIME_OPTIONS.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {newTask.time && !newTask.assigned_to && (
              <Select value={String(newTask.duration_minutes || 60)} onValueChange={(v) => setNewTask({ ...newTask, duration_minutes: parseInt(v, 10) })}>
                <SelectTrigger className={PILL_CLS} data-testid="add-task-duration-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DURATION_OPTIONS.map((d) => (
                    <SelectItem key={d.value} value={String(d.value)}>{d.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Select value={newTask.priority} onValueChange={(v) => setNewTask({ ...newTask, priority: v })}>
              <SelectTrigger className={PILL_CLS} data-testid="add-task-priority-pill">
                <Flag className="w-3 h-3" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
              </SelectContent>
            </Select>
            <AssigneeControls
              value={newTask.assigned_to}
              valueName={newTask.assigned_to_name}
              members={canAssign ? members : []}
              currentUserId={currentUserId}
              testIdPrefix="add-task"
              onChange={(m) => (m
                ? setNewTask({ ...newTask, assigned_to: m.user_id, assigned_to_name: m.user_name })
                : clearAssignee())}
            />
          </div>
          {newTask.assigned_to && (
            <p className="text-[11px] text-slate-400 dark:text-slate-500">
              Shared task — {newTask.assigned_to === currentUserId ? 'appears in your Shared Tasks' : `${newTask.assigned_to_name} will be notified`}. Due {format(effectiveDate, 'MMM d')}.
              {newTask.isRecurring && ' Repeats — one occurrence at a time.'}
            </p>
          )}

          {/* Sub-tasks + Repeat — always visible, repeat expands via its toggle */}
          <div className="border border-slate-200 dark:border-white/10 rounded-xl divide-y divide-slate-100 dark:divide-white/10 overflow-hidden">
            <div className="studio-box px-3 py-2.5" data-testid="add-task-subtasks-section">
              <SubtaskEditor
                items={newTask.subtasks || []}
                onChange={(items) => setNewTask({ ...newTask, subtasks: items })}
                note={newTask.isRecurring ? 'Sub-tasks reset fresh on every occurrence.' : null}
              />
            </div>
            <RecurrenceOptions data={newTask} setData={setNewTask} bare />
          </div>
        </div>
        <DialogFooter className="px-5 py-3 bg-slate-50 dark:bg-[#1e1f22] border-t flex-shrink-0">
          <span className="text-[10px] text-slate-400 dark:text-slate-500 mr-auto self-center hidden sm:inline">
            {navigator.platform?.includes('Mac') ? '⌘' : 'Ctrl'}+Enter
          </span>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="h-9">Cancel</Button>
          <Button onClick={onSubmit} className="bg-indigo-600 hover:bg-indigo-700 h-9" data-testid="add-task-submit-btn">
            {newTask.isRecurring && <Repeat className="w-4 h-4 mr-1" />}
            {newTask.assigned_to && <Users className="w-4 h-4 mr-1" />}
            {newTask.assigned_to ? 'Assign Task' : 'Add Task'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Shared Recurrence Options UI ──
export function RecurrenceOptions({ data, setData, bare = false }) {
  // The server refuses an end date before the task's due date, so the picker
  // floor is the later of today and whatever due date the form carries
  // (`due_date` on a shared task, `date` on a personal one). Falls back to
  // today when the form has no date yet.
  const today = format(new Date(), 'yyyy-MM-dd');
  const due = data.due_date || data.date;
  const endDateMin = (typeof due === 'string' && due > today) ? due : today;
  return (
    <div className={bare ? '' : 'border border-slate-200 dark:border-white/10 rounded-lg overflow-hidden'}>
      <button
        type="button"
        onClick={() => setData({ ...data, isRecurring: !data.isRecurring })}
        className="w-full flex items-center justify-between p-3 hover:bg-slate-50 dark:hover:bg-white/5 transition-colors"
        data-testid="recurrence-toggle"
      >
        <div className="flex items-center gap-2">
          <Repeat className="studio-icon-fg w-4 h-4 text-indigo-600 dark:text-indigo-400" />
          <span className="text-sm font-medium">Repeat</span>
        </div>
        <div className={`studio-switch w-9 h-5 rounded-full transition-colors ${data.isRecurring ? 'bg-indigo-600' : 'bg-slate-200 dark:bg-[#3e4145]'}`}>
          <div className={`w-4 h-4 rounded-full bg-card shadow-sm transform transition-transform mt-0.5 ${data.isRecurring ? 'translate-x-4 ml-0.5' : 'translate-x-0.5'}`} />
        </div>
      </button>
      {data.isRecurring && (
        <div className="px-3 pb-3 pt-2 border-t border-slate-100 dark:border-white/10 bg-slate-50 dark:bg-[#1e1f22] space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5 block">Frequency</Label>
              <Select value={data.recurrenceType || 'weekly'} onValueChange={(v) => setData({ ...data, recurrenceType: v })}>
                <SelectTrigger className="h-8 text-sm bg-card"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5 block">Every</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number" min="1" max="30"
                  value={data.recurrenceInterval}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '') {
                      setData({ ...data, recurrenceInterval: '' });
                    } else {
                      const num = parseInt(raw, 10);
                      if (!isNaN(num)) setData({ ...data, recurrenceInterval: num });
                    }
                  }}
                  onBlur={() => {
                    const num = parseInt(data.recurrenceInterval, 10);
                    setData({ ...data, recurrenceInterval: (!num || num < 1) ? 1 : Math.min(num, 30) });
                  }}
                  className="h-8 text-sm bg-card w-16"
                />
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  {data.recurrenceType === 'daily' ? 'days' : data.recurrenceType === 'weekly' ? 'weeks' : data.recurrenceType === 'monthly' ? 'months' : 'years'}
                </span>
              </div>
            </div>
          </div>
          {data.recurrenceType === 'weekly' && (
            <div>
              <Label className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5 block">On days</Label>
              <div className="flex flex-wrap gap-1.5">
                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, idx) => {
                  const dayFull = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][idx];
                  const isSelected = data.recurrenceDaysOfWeek?.includes(dayFull);
                  return (
                    <button
                      key={idx} type="button"
                      onClick={() => {
                        const days = isSelected
                          ? (data.recurrenceDaysOfWeek || []).filter(d => d !== dayFull)
                          : [...(data.recurrenceDaysOfWeek || []), dayFull];
                        setData({ ...data, recurrenceDaysOfWeek: days });
                      }}
                      className={`w-8 h-8 rounded-full text-xs font-medium transition-colors ${
                        isSelected ? 'bg-indigo-600 text-white' : 'bg-card text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-white/10 hover:border-indigo-300'
                      }`}
                    >
                      {day}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {data.recurrenceType === 'monthly' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5 block">Repeats on</Label>
                <Select
                  value={data.recurrenceMonthlyMode || 'day'}
                  onValueChange={(v) => setData({ ...data, recurrenceMonthlyMode: v })}
                >
                  <SelectTrigger className="h-8 text-sm bg-card" data-testid="monthly-mode-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="day">Day of month</SelectItem>
                    <SelectItem value="nth_weekday">Specific weekday (e.g. 4th or last Thursday)</SelectItem>
                    <SelectItem value="last_day">Last day of month</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {(data.recurrenceMonthlyMode || 'day') === 'day' && (
                <div>
                  <Label className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5 block">On day</Label>
                  <Select value={String(data.recurrenceDayOfMonth || 1)} onValueChange={(v) => setData({ ...data, recurrenceDayOfMonth: parseInt(v) })}>
                    <SelectTrigger className="w-20 h-8 text-sm bg-card"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 31 }, (_, i) => (
                        <SelectItem key={i + 1} value={String(i + 1)}>{i + 1}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {data.recurrenceMonthlyMode === 'nth_weekday' && (
                <div>
                  <Label className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5 block">Which one</Label>
                  <div className="flex gap-2">
                    <Select value={String(data.recurrenceNth || 1)} onValueChange={(v) => setData({ ...data, recurrenceNth: v === 'last' ? 'last' : parseInt(v) })}>
                      <SelectTrigger className="h-8 text-sm bg-card w-24" data-testid="monthly-nth-trigger"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1st</SelectItem>
                        <SelectItem value="2">2nd</SelectItem>
                        <SelectItem value="3">3rd</SelectItem>
                        <SelectItem value="4">4th</SelectItem>
                        <SelectItem value="last">Last</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={data.recurrenceWeekday || 'friday'} onValueChange={(v) => setData({ ...data, recurrenceWeekday: v })}>
                      <SelectTrigger className="h-8 text-sm bg-card flex-1" data-testid="monthly-nth-weekday-trigger"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].map((d) => (
                          <SelectItem key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5 block">Ends</Label>
              <Select value={data.recurrenceEndType || 'never'} onValueChange={(v) => setData({ ...data, recurrenceEndType: v })}>
                <SelectTrigger className="h-8 text-sm bg-card"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="never">Never</SelectItem>
                  <SelectItem value="date">On date</SelectItem>
                  <SelectItem value="count">After #</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {data.recurrenceEndType === 'date' && (
              <div>
                <Label className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5 block">End Date</Label>
                <Input type="date" min={endDateMin} value={data.recurrenceEndDate || ''} onChange={(e) => setData({ ...data, recurrenceEndDate: e.target.value })} className="h-8 text-sm bg-card" />
              </div>
            )}
            {data.recurrenceEndType === 'count' && (
              <div>
                <Label className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5 block">Occurrences</Label>
                <Input type="number" min="1" max="365"
                  value={data.recurrenceCount}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '') {
                      setData({ ...data, recurrenceCount: '' });
                    } else {
                      const num = parseInt(raw, 10);
                      if (!isNaN(num)) setData({ ...data, recurrenceCount: num });
                    }
                  }}
                  onBlur={() => {
                    const num = parseInt(data.recurrenceCount, 10);
                    setData({ ...data, recurrenceCount: (!num || num < 1) ? 1 : Math.min(num, 365) });
                  }}
                  className="h-8 text-sm bg-card w-20"
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Delete Recurring Task Dialog
export function DeleteRecurringDialog({ 
  open, 
  onOpenChange, 
  taskToDelete, 
  deleteTask 
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader className="pb-2">
          <DialogTitle className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-red-100 dark:bg-red-900/50 flex items-center justify-center">
              <RotateCcw className="w-5 h-5 text-red-600 dark:text-red-400" />
            </div>
            <span>Delete Recurring Task</span>
          </DialogTitle>
          <DialogDescription>
            This is a recurring task. How would you like to delete it?
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-4">
          <Button 
            variant="outline" 
            className="w-full justify-start h-auto p-4 rounded-xl hover:bg-slate-50 dark:hover:bg-white/5"
            onClick={() => deleteTask(taskToDelete?.id, 'single')}
            data-testid="delete-scope-single-btn"
          >
            <div className="text-left">
              <div className="font-medium">Delete this occurrence only</div>
              <div className="text-sm text-slate-500 dark:text-slate-400">Only this single instance will be deleted</div>
            </div>
          </Button>
          <Button 
            variant="outline" 
            className="w-full justify-start h-auto p-4 rounded-xl hover:bg-slate-50 dark:hover:bg-white/5"
            onClick={() => deleteTask(taskToDelete?.id, 'future')}
            data-testid="delete-scope-future-btn"
          >
            <div className="text-left">
              <div className="font-medium">Delete this and future occurrences</div>
              <div className="text-sm text-slate-500 dark:text-slate-400">This and all future instances will be deleted</div>
            </div>
          </Button>
          <Button 
            variant="outline" 
            className="w-full justify-start h-auto p-4 rounded-xl border-red-200 dark:border-red-900 hover:bg-red-50 dark:hover:bg-red-500/15"
            onClick={() => deleteTask(taskToDelete?.id, 'all')}
            data-testid="delete-scope-all-btn"
          >
            <div className="text-left">
              <div className="font-medium text-red-600 dark:text-red-400">Delete all occurrences</div>
              <div className="text-sm text-red-400">All instances of this recurring task will be deleted</div>
            </div>
          </Button>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Edit Task Dialog
export function EditTaskDialog({ 
  open, 
  onOpenChange, 
  editingTaskData, 
  setEditingTaskData, 
  updateTask,
  subtasksApi,
  members = [],
  currentUserId,
}) {
  const [showScope, setShowScope] = React.useState(false);

  // R34 (2026-09-07): assigning an EXISTING task moves it to Shared Tasks
  // (Mongo) — see useTaskEdit 'assign'. Not offered for completed tasks
  // (shared items are always created open) or for the parent row of a
  // recurring series (deleting the parent would orphan the series); a single
  // occurrence may be assigned (it leaves the series).
  const canAssignHere = members.length > 0 && !editingTaskData?.completed && !editingTaskData?.isRecurringParent;
  const assigning = !!editingTaskData?.assigned_to;
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSave();
    }
  };

  const isRecurringTask = editingTaskData?.originalIsRecurring || editingTaskData?.originalParentTaskId;

  const handleSave = () => {
    if (assigning) {
      // R34: move to Shared Tasks (create shared item, then delete the row)
      updateTask('assign');
    } else if (isRecurringTask && !editingTaskData?.isRecurring) {
      // Recurring toggled OFF → stop recurrence, skip scope popup
      updateTask('stop_recurring');
    } else if (isRecurringTask) {
      // Still recurring — ask for scope
      setShowScope(true);
    } else if (editingTaskData?.isRecurring && !editingTaskData?.originalIsRecurring) {
      // Converting non-recurring → recurring: treat as "create new recurring"
      updateTask('convert_to_recurring');
    } else {
      updateTask('single');
    }
  };

  const handleScopeSelect = (scope) => {
    setShowScope(false);
    updateTask(scope);
  };

  const selectedDate = editingTaskData?.date ? new Date(editingTaskData.date + 'T00:00:00') : new Date();

  // Scope selection sub-dialog
  if (showScope) {
    return (
      <Dialog open={open} onOpenChange={(v) => { if (!v) { setShowScope(false); } onOpenChange(v); }}>
        <DialogContent className="max-w-md">
          <DialogHeader className="pb-2">
            <DialogTitle className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center">
                <Repeat className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              </div>
              <span>Update Recurring Task</span>
            </DialogTitle>
            <DialogDescription>
              This is a recurring task. How should this change apply?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-4">
            <Button
              variant="outline"
              className="w-full justify-start h-auto p-4 rounded-xl whitespace-normal hover:bg-slate-50 dark:hover:bg-white/5"
              onClick={() => handleScopeSelect('single')}
              data-testid="scope-single-btn"
            >
              <div className="text-left">
                <div className="font-medium">This occurrence only</div>
                <div className="text-sm text-slate-500 dark:text-slate-400">Only update this single instance</div>
              </div>
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start h-auto p-4 rounded-xl whitespace-normal hover:bg-slate-50 dark:hover:bg-white/5"
              onClick={() => handleScopeSelect('future')}
              data-testid="scope-future-btn"
            >
              <div className="text-left">
                <div className="font-medium">This and future occurrences</div>
                <div className="text-sm text-slate-500 dark:text-slate-400">Update this and regenerate all future instances</div>
              </div>
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start h-auto p-4 rounded-xl whitespace-normal hover:bg-slate-50 dark:hover:bg-white/5"
              onClick={() => handleScopeSelect('all')}
              data-testid="scope-all-btn"
            >
              <div className="text-left">
                <div className="font-medium">All occurrences</div>
                <div className="text-sm text-slate-500 dark:text-slate-400">Update the whole series — history is kept</div>
              </div>
            </Button>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowScope(false)}>Back</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader className="pb-2">
          <DialogTitle className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center">
              <CalendarIcon className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            </div>
            <span>Edit Task</span>
          </DialogTitle>
          <DialogDescription>
            Update your task details or reschedule to a different date
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label className="text-sm font-medium">Task Title</Label>
            <Input
              placeholder="e.g., Review song list"
              value={editingTaskData?.title || ''}
              onChange={(e) => setEditingTaskData({ ...editingTaskData, title: e.target.value })}
              onKeyDown={handleKeyDown}
              className="h-11"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium">Description (optional)</Label>
            <Textarea
              placeholder="Add more details..."
              value={editingTaskData?.description || ''}
              onChange={(e) => setEditingTaskData({ ...editingTaskData, description: e.target.value })}
              onKeyDown={handleKeyDown}
            />
          </div>
          
          {/* Same field as the Add dialog; past dates stay disabled when rescheduling */}
          <ScheduledDateField
            value={editingTaskData?.date || null}
            onChange={(date) => setEditingTaskData({ ...editingTaskData, date })}
            disablePast
            testIdPrefix="edit-task"
          />

          {/* Compact pill row: time / duration / priority / assign */}
          <div className="flex flex-wrap items-center gap-2">
            {!assigning && <Select value={editingTaskData?.time || 'none'} onValueChange={(v) => setEditingTaskData({ ...editingTaskData, time: v === 'none' ? '' : v, duration_minutes: v === 'none' ? null : (editingTaskData?.duration_minutes || 60) })}>
              <SelectTrigger className={PILL_CLS} data-testid="edit-task-time-pill">
                <Clock className="w-3 h-3" />
                <SelectValue>{editingTaskData?.time ? formatTime12h(editingTaskData.time) : 'No time'}</SelectValue>
              </SelectTrigger>
              <SelectContent className="max-h-60">
                <SelectItem value="none">No time</SelectItem>
                {TIME_OPTIONS.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>}
            {!assigning && editingTaskData?.time && (
              <Select value={String(editingTaskData?.duration_minutes || 60)} onValueChange={(v) => setEditingTaskData({ ...editingTaskData, duration_minutes: parseInt(v, 10) })}>
                <SelectTrigger className={PILL_CLS} data-testid="edit-task-duration-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DURATION_OPTIONS.map((d) => (
                    <SelectItem key={d.value} value={String(d.value)}>{d.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Select value={editingTaskData?.priority || 'none'} onValueChange={(v) => setEditingTaskData({ ...editingTaskData, priority: v })}>
              <SelectTrigger className={PILL_CLS} data-testid="edit-task-priority-pill">
                <Flag className="w-3 h-3" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
              </SelectContent>
            </Select>
            {canAssignHere && (
              <AssigneeControls
                value={editingTaskData?.assigned_to}
                valueName={editingTaskData?.assigned_to_name}
                members={members}
                currentUserId={currentUserId}
                testIdPrefix="edit-task"
                onChange={(m) => setEditingTaskData({
                  ...editingTaskData,
                  assigned_to: m ? m.user_id : null,
                  assigned_to_name: m ? m.user_name : '',
                })}
              />
            )}
          </div>
          {assigning && (
            <p className="text-[11px] text-slate-400 dark:text-slate-500" data-testid="edit-task-assign-hint">
              Moves to Shared Tasks — {editingTaskData.assigned_to === currentUserId ? 'appears in your Shared Tasks' : `${editingTaskData.assigned_to_name} will be notified`}.
              Due {format(selectedDate, 'MMM d')}{isRecurringTask ? '; this occurrence leaves the repeating series' : ''}. Time and repeat settings are dropped; sub-tasks come along.
            </p>
          )}

          {/* Sub-tasks + Repeat — always visible, repeat expands via its toggle */}
          {editingTaskData && !assigning && (
            <div className="border border-slate-200 dark:border-white/10 rounded-xl divide-y divide-slate-100 dark:divide-white/10 overflow-hidden">
              {subtasksApi && editingTaskData?.id && (() => {
                const st = subtasksApi.getForTask(editingTaskData);
                return (
                  <div className="px-3 py-2.5" data-testid="edit-task-subtasks-section">
                    <SubtaskEditor
                      items={st.items}
                      onChange={(items) => subtasksApi.saveItems(editingTaskData.id, items)}
                      checkedIds={st.checkedIds}
                      onToggleCheck={(subId, checked) => subtasksApi.toggleCheck(editingTaskData.id, editingTaskData.id, subId, checked)}
                      showChecks
                      note={isRecurringTask ? 'Each occurrence gets its own fresh, unchecked copy.' : null}
                    />
                  </div>
                );
              })()}
              <RecurrenceOptions data={editingTaskData} setData={setEditingTaskData} bare />
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => { onOpenChange(false); setEditingTaskData(null); }}>Cancel</Button>
          <Button onClick={handleSave} className="bg-indigo-500 hover:bg-indigo-600" data-testid="save-task-btn">
            {assigning ? <Users className="w-4 h-4 mr-1" /> : (editingTaskData?.isRecurring && <Repeat className="w-4 h-4 mr-1" />)}
            {assigning ? 'Assign Task' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Edit Annual Goal Dialog
export function EditGoalDialog({ 
  open, 
  onOpenChange, 
  editingGoalData, 
  setEditingGoalData, 
  updateAnnualGoal, 
  currentDate 
}) {
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      updateAnnualGoal();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Annual Goal</DialogTitle>
          <DialogDescription>
            Update your goal for {currentDate.getFullYear()}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Goal Title</Label>
            <Input
              placeholder="e.g., Grow worship team to 20 members"
              value={editingGoalData?.title || ''}
              onChange={(e) => setEditingGoalData({ ...editingGoalData, title: e.target.value })}
              onKeyDown={handleKeyDown}
            />
          </div>
          <div className="space-y-2">
            <Label>Description (optional)</Label>
            <Textarea
              placeholder="Add more details..."
              value={editingGoalData?.description || ''}
              onChange={(e) => setEditingGoalData({ ...editingGoalData, description: e.target.value })}
              onKeyDown={handleKeyDown}
            />
          </div>
          <div className="space-y-2">
            <Label>By which quarter?</Label>
            <Select
              value={editingGoalData?.quarter || 'none'}
              onValueChange={(value) => setEditingGoalData({ ...editingGoalData, quarter: value === 'none' ? null : value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a quarter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                <SelectItem value="Q1">Q1 (Jan - Mar)</SelectItem>
                <SelectItem value="Q2">Q2 (Apr - Jun)</SelectItem>
                <SelectItem value="Q3">Q3 (Jul - Sep)</SelectItem>
                <SelectItem value="Q4">Q4 (Oct - Dec)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { onOpenChange(false); setEditingGoalData(null); }}>Cancel</Button>
          <Button onClick={updateAnnualGoal} className="bg-indigo-500 hover:bg-indigo-600">Save Changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Edit Priority Dialog
export function EditPriorityDialog({ 
  open, 
  onOpenChange, 
  editingPriorityData, 
  setEditingPriorityData, 
  updatePriority, 
  currentDate 
}) {
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      updatePriority();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Monthly Priority</DialogTitle>
          <DialogDescription>
            Update your priority for {format(currentDate, 'MMMM yyyy')}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Priority Title</Label>
            <Input
              placeholder="e.g., Plan Easter service setlist"
              value={editingPriorityData?.title || ''}
              onChange={(e) => setEditingPriorityData({ ...editingPriorityData, title: e.target.value })}
              onKeyDown={handleKeyDown}
            />
          </div>
          <div className="space-y-2">
            <Label>Description (optional)</Label>
            <Textarea
              placeholder="Add more details..."
              value={editingPriorityData?.description || ''}
              onChange={(e) => setEditingPriorityData({ ...editingPriorityData, description: e.target.value })}
              onKeyDown={handleKeyDown}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { onOpenChange(false); setEditingPriorityData(null); }}>Cancel</Button>
          <Button onClick={updatePriority} className="bg-indigo-500 hover:bg-indigo-600">Save Changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Move Task Dialog
export function MoveTaskDialog({ 
  open, 
  onOpenChange, 
  taskToMove, 
  setTaskToMove, 
  moveTaskToDay, 
  resetTaskToDefault, 
  taskDayOverrides 
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Move Suggested Task</DialogTitle>
          <DialogDescription className="text-sm">
            {taskToMove && (
              <span className="block mt-2 p-2 bg-slate-100 dark:bg-[#3a3d41] rounded text-slate-700 dark:text-slate-200 font-medium">
                "{taskToMove}"
              </span>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <Label className="text-sm text-slate-500 dark:text-slate-400 mb-3 block">Move this task to appear every:</Label>
          <div className="grid grid-cols-2 gap-2">
            {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day) => (
              <Button
                key={day}
                variant="outline"
                className="justify-start h-auto py-3"
                onClick={() => moveTaskToDay(day)}
              >
                <CalendarIcon className="w-4 h-4 mr-2 text-indigo-500" />
                {day}
              </Button>
            ))}
          </div>
          {taskToMove && taskDayOverrides[taskToMove] && (
            <Button
              variant="ghost"
              className="w-full mt-4 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-100"
              onClick={() => resetTaskToDefault(taskToMove)}
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Reset to default day
            </Button>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { onOpenChange(false); setTaskToMove(null); }}>Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
